/**
 * Battle time model values (art-direction §7.4 = GDD N31, C85, C200) and the engine constants the renderer reads
 * (smear, frame-break cap, flash limiter). Owner: Engine (F2). The composition lives in `src/shared/cine/timeModel.ts`
 * (P2); this file only holds the numbers of record. Pure data.
 */

/** Phase kinds of creatures §C3.12 (the `playback` scale is phase-aware). */
export type PhaseKind = 'approach' | 'retreat' | 'anticipation' | 'strike' | 'recovery' | 'idleBeat' | 'status' | 'reaction' | 'cinematic' | 'ko';
export type BattleSpeed = 0.75 | 1 | 2;

/** §7.4 `playback`: rate(kind, speed). ×1 is 1 for every kind; ×0.75 is 0.75 except `ko`. */
export const PLAYBACK_RATE_X2: Record<PhaseKind, number> = {
  approach: 2.5, retreat: 2.5, anticipation: 1.25, strike: 1.5, recovery: 2.5, idleBeat: 3.0, status: 2.5, reaction: 2.0, cinematic: 1.5, ko: 1,
};
export function playbackRate(kind: PhaseKind, speed: BattleSpeed): number {
  if (kind === 'ko') return 1;
  if (speed === 2) return PLAYBACK_RATE_X2[kind];
  return speed;
}
/** §7.4 `vfxRate` (world systems without a phase). */
export const VFX_RATE: Record<BattleSpeed, number> = { 0.75: 0.75, 1: 1, 2: 1.5 };

/** The five numbers every doc states identically (AT-35). */
export const TIME_MODEL = {
  /** 1. KO hit-stop, ms, any class, any speed. */
  koHitStopMs: 233,
  /** 2. `koSlowmo`: world scale 0.35 for 600 ms after the KO hit-stop, then linear to 1 over 200 ms. */
  koSlowmo: { scale: 0.35, holdMs: 600, rampMs: 200 },
  /** 3. `reactDilation` «Замах»: 0.35 for reactMs (×1 1100, ×2 800) + modifiers; in 60 ms, out 80 ms. */
  reactDilation: { scale: 0.35, ms: { 1: 1100, 2: 800 } as Record<1 | 2, number>, inMs: 60, outMs: 80, tattletaleMs: 250, owlHeadMs: 60, itemsMaxMs: 100 },
  /** 4. `omenDilation`: ×1 0.25 for 1050 ms (seal 800); ×2 0.35 for 735 ms (seal 560); in/out 80 ms. */
  omenDilation: { x1: { scale: 0.25, ms: 1050, sealMs: 800 }, x2: { scale: 0.35, ms: 735, sealMs: 560 }, inMs: 80, outMs: 80 },
  /** 5. Hit-stop at ×2 = max(50, round(0.75 × the ×1 value)); KO stays 233. */
  hitStopX2: { factor: 0.75, minMs: 50 },
  /** Non-answerable windup hold (ms at world 1) per speed. */
  windupHoldMs: { 0.75: 300, 1: 300, 2: 200 } as Record<BattleSpeed, number>,
  /** Particles keep simulating at this rate while any hit-stop is active. */
  particleHitStop: 0.2,
} as const;

/** Hit-stop at a battle speed (§8.2.1 footnote). */
export function hitStopAtSpeed(msAtX1: number, speed: BattleSpeed, isKo = false): number {
  if (isKo) return TIME_MODEL.koHitStopMs;
  if (speed === 2) return Math.max(TIME_MODEL.hitStopX2.minMs, Math.round(TIME_MODEL.hitStopX2.factor * msAtX1));
  return msAtX1;
}
/** `koSlowmo` world scale at `ms` after the KO hit-stop ended. */
export function koSlowmoScale(ms: number): number {
  const k = TIME_MODEL.koSlowmo;
  if (ms < 0) return 1;
  if (ms < k.holdMs) return k.scale;
  if (ms < k.holdMs + k.rampMs) return k.scale + ((ms - k.holdMs) / k.rampMs) * (1 - k.scale);
  return 1;
}

/** Smear (art §4.1-J, architecture §5.7): lag window, cap, threshold, hit-stop hold; `smear[slot]` = (k, lagS, capH, thresholdHps). */
export const SMEAR = { lagMs: 26.7, capH: 0.35, thresholdHps: 4, defaultK: 1.0, kRange: [0.5, 1.4] as const, holdMs: 16.7 } as const;

/** Figures allowed to break the miniature frame at once (art §4.10 = GDD §4.15). */
export const FRAME_BREAK_CAP = 2;
/** Frame-break priority list (art §4.10), high → low. */
export const FRAME_BREAK_PRIORITY = ['koKnockback', 'ultimate', 'marginalSummon', 'bossBeat', 'marginEntrance', 'petKoFloat', 'koSoul'] as const;
/** A newcomer that does not outrank a holder waits this long, then plays degraded. */
export const FRAME_BREAK_WAIT_MS = 200;

/** Impact frames and the flash limiter (art §4.13; WCAG 2.3.1). */
export const IMPACT = {
  negativeMs: 33, whiteMs: 17, whiteMix: 0.35, maxFlashMsPer1s: 50, minGapMs: 600,
  /** «Вспышки» off / reduced motion → vignette pulse. */
  pulseMs: 120, pulseStrength: 0.25,
} as const;
/** Panel shake (art §7.4). */
export const SHAKE = { decayPerS: 2.2, maxPx: 14, refPanelW: 358, noiseHz: 30, rotDeg: 0.6, traumaPer10Px: 1 } as const;
/** Zoom punch envelope (art §7.4). */
export const PUNCH = { inMs: 80, outMs: 300 } as const;
/** Ink-wipe scene transition (art §7.4). */
export const INK_WIPE = { coverMs: 350, revealMs: 350, reducedCrossfadeMs: 150 } as const;
/** Sketch-to-paint reveal (art §4.1-I). */
export const REVEAL = { partMs: 450, fadeToSketchMs: 800, redrawMs: 1200 } as const;
